import first from 'lodash/first';
import get from 'lodash/get';
import map from 'lodash/map';
import { type Observable as ObservableType, Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import { BAD_REQUEST } from '@atlassian/jira-common-constants/src/http-status-codes.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { isClientFetchError as getIsClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { isUserNetworkError } from '@atlassian/jira-software-sla-tracker/src/services/response-to-sla/index.tsx';
import { extractStatusCode } from '@atlassian/jira-software-sla-tracker/src/services/status-code/index.tsx';
import { showErrorFlag } from '@atlassian/jira-software-swag/src/common/constants.tsx';
import {
	type SwagError,
	isSwagError,
	isFetchError,
	isJson,
} from '@atlassian/jira-software-swag/src/common/utils/error/index.tsx';
import { REFRESH_SOURCE_FAILURE } from '../../model/constants.tsx';
import { GlobalIssueCreateType } from '../../model/modals/types.tsx';
import { cardCreateService } from '../../services/card-create/index.tsx';
import { makeServiceContext } from '../../services/service-context/index.tsx';
import {
	type LoadAppDataAction,
	loadAppData,
	refreshDataRequest,
} from '../../state/actions/app/index.tsx';
import {
	type CardCreateSuccessAction,
	type CardCreateMultipleRequestAction,
	CARD_CREATE_MULTIPLE_REQUEST,
	cardCreateSuccess,
	cardCreateFailure,
	cardCreateUnmapped,
} from '../../state/actions/cards/create/index.tsx';
import type { Action } from '../../state/actions/index.tsx';
import { openModal } from '../../state/actions/modals/index.tsx';
import { type RefetchVersionsAction, refetchVersions } from '../../state/actions/version/index.tsx';
import { isCmpKanbanProject } from '../../state/selectors/app/index.tsx';
import { getInlineCreateSessionId } from '../../state/selectors/inline-create/index.tsx';
import {
	areStatusesFromSameColumn,
	isStatusMappedToBoardOld,
	makeGetKanbanSingleColumnStatusId,
} from '../../state/selectors/statuses/index.tsx';
import type { MiddlewareAPI, ActionsObservable } from '../../state/types.tsx';
import { setCardIdWithAboveICCOpen } from '../../state/actions/cards/rich-card/index.tsx';
import { getCardIdWithAboveICCOpen } from '../../state/selectors/cards/index.tsx';
import { DRAG_START } from '../../state/actions/drag/index.tsx';

const parseFirstFetchErrorMessage = (error: FetchError): string | null => {
	if (isJson(error.message)) {
		const response = JSON.parse(error.message);
		return (
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(first(map(get(response, 'errors', null))) as string) ??
			get<string | null>(response, 'generalErrors[0]', null) ??
			null
		);
	}
	return error.message;
};

const getBadRequestErrorMessage = (error: FetchError | SwagError): string | null => {
	if (isSwagError(error)) {
		const badRequestError = error.graphQLErrors.find(
			({ extensions: { statusCode } }) => statusCode === BAD_REQUEST,
		);
		return badRequestError ? badRequestError.message : null;
	}
	if (isFetchError(error) && error.statusCode === BAD_REQUEST) {
		return parseFirstFetchErrorMessage(error);
	}
	return null;
};

export const cardCreateMultipleGqlEpic = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
): ObservableType<Action> =>
	action$
		.ofType(CARD_CREATE_MULTIPLE_REQUEST)
		.mergeMap((action: CardCreateMultipleRequestAction) => {
			const {
				payload: {
					listId,
					insertBefore,
					storeFieldsFromCardCreateData,
					storeFieldsFromFetchIssuesData,
					tempIds,
					summaries,
					typeId,
					parentId,
					fixVersions,
					descriptions,
				},
				meta: {
					optimistic: { id: optimisticId },
					analyticsEvent,
				},
			} = action;
			const state = store.getState();

			const serviceContext = makeServiceContext(state);
			const inlineCreateSessionId = getInlineCreateSessionId(state);

			return cardCreateService(
				serviceContext,
				store,
				summaries,
				typeId,
				parentId,
				fixVersions,
				listId,
				insertBefore,
				descriptions,
				storeFieldsFromCardCreateData,
				storeFieldsFromFetchIssuesData,
			)
				.flatMap((cardsCreated): ObservableType<Action> => {
					if (cardsCreated === null) {
						return Observable.of(
							cardCreateUnmapped(summaries.length, null, optimisticId, null, analyticsEvent),
						);
					}
					if (!isStatusMappedToBoardOld(cardsCreated[0].statusId)(state)) {
						return Observable.of(
							cardCreateUnmapped(
								summaries.length,
								cardsCreated.map((cardCreated) => cardCreated.key),
								optimisticId,
								cardsCreated[0].issueTypeName,
								analyticsEvent,
							),
						);
					}
					const actions: Array<
						CardCreateSuccessAction | LoadAppDataAction | RefetchVersionsAction
					> = cardsCreated.map((cardCreated, index) =>
						cardCreateSuccess(
							cardCreated,
							listId,
							tempIds[index],
							insertBefore !== null,
							summaries.length > 1,
							optimisticId,
							analyticsEvent,
						),
					);

					// We should refetch versions data when an issue is inline created to keep the version panel on sync
					// and to avoid this issue: https://hello.atlassian.net/browse/FEBA-1548a
					actions.push(refetchVersions());

					if (isCmpKanbanProject(state)) {
						const cardListStatusId = makeGetKanbanSingleColumnStatusId(state)(listId);

						if (
							cardListStatusId &&
							!areStatusesFromSameColumn(state)(cardListStatusId, cardsCreated[0].statusId)
						) {
							actions.push(loadAppData());
						}
					}

					return Observable.from(actions);
				})
				.catch((error: SwagError | FetchError): ObservableType<Action> => {
					const validationErrorMessage = getBadRequestErrorMessage(error);
					const statusCode = extractStatusCode(error);
					const isClientFetchError = getIsClientFetchError(error) || isUserNetworkError(error);

					if (validationErrorMessage) {
						return Observable.from([
							openModal({
								type: GlobalIssueCreateType,
								payload: {
									cardListId: listId,
									cardParentId: parentId,
									cardSummary: summaries[0],
									cardTypeId: typeId,
									rankBeforeCardId: insertBefore,
									fixVersions,
									submitErrorMessage: validationErrorMessage,
									sessionId: inlineCreateSessionId,
								},
							}),
							cardCreateFailure(
								summaries.length,
								optimisticId,
								error.message,
								statusCode,
								isClientFetchError,
							),
						]);
					}

					return Observable.from([
						cardCreateFailure(
							summaries.length,
							optimisticId,
							error.message,
							statusCode,
							isClientFetchError,
							analyticsEvent,
						),
						showErrorFlag(error),
						refreshDataRequest(REFRESH_SOURCE_FAILURE),
					]);
				});
		});

export const closeCardCreateOnDragStart = (
	action$: ActionsObservable,
	store: MiddlewareAPI,
): ObservableType<Action> =>
	action$.ofType(DRAG_START).mergeMap(() => {
		const state = store.getState();
		const cardIdWithAboveICCOpen = getCardIdWithAboveICCOpen(state);

		if (cardIdWithAboveICCOpen) {
			return Observable.of(setCardIdWithAboveICCOpen(null));
		}
		return Observable.empty<never>();
	});
